import { Calendar, ChevronDown, Clock, Stamp } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { getActivity } from '../../services/activityService';
import Loader from '../Loader/Loader';

interface Activity {
  id: number;
  name: string;
  role: string;
  avatar: string | null;
  description: string;
  reference_number: string;
  action: string;
  date: string;
  time: string;
}

interface ActivitiesProps {
  moduleName: string;
  moduleItemId: number;
}

const Activities: React.FC<ActivitiesProps> = ({ moduleName, moduleItemId }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const data = await getActivity(moduleName, moduleItemId);

        const mappedActivities: Activity[] = data.map((item: any) => {
          const createdAt = new Date(item.created_at);

          return {
            id: item.id,
            action: item.action,
            name: item.user,
            role: item.role
              ?.split('_')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
            // avatar: `https://i.pravatar.cc/150?u=${item.user}`,
            avatar: item.profile_photo || null,
            description: item.data?.remarks ?? '',
            reference_number: item.data?.reference_number || '',
            date: createdAt.toLocaleDateString('en-GB'),
            time: createdAt.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            }),
          };
        });

        setActivities(mappedActivities);
      } catch (error) {
        console.error('Failed to fetch activities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [moduleName, moduleItemId]);

  if (loading) return <Loader />;

  return (
    <div className="p-6 w-[350px] ml-auto mr-[-48px]">
      <h2 className="text-blue-600 font-semibold mb-4">Activities</h2>

      <div className="flex flex-col gap-4">
        {activities.map((activity) => (
          <div key={activity.id} className="rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Status Header */}
            <div className="flex items-center justify-between bg-indigo-300 px-4 py-2">
              <span className="text-white font-medium">{activity.action === 'create' ? 'Application Created' : activity.action}</span>
              <div className="bg-white p-2 rounded-full shadow-sm">
                <Stamp className="w-5 h-5 text-indigo-600" />
              </div>
            </div>

            {/* Content */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {activity.avatar ? (
                  <img src={activity.avatar} alt={activity.name} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-lg font-medium text-gray-600">
                    {activity.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-800">{activity.name}</h3>
                  <p className="text-sm text-gray-500">{activity.role}</p>
                  <p className="text-sm text-gray-600">{activity.reference_number}</p>
                </div>
              </div>

              <button
                onClick={() => setOpenId(openId === activity.id ? null : activity.id)}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
              >
                <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${openId === activity.id ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Dropdown Section (Date & Time) */}
            {/* {openId === activity.id && (
              <div className="flex gap-6 items-center px-6 py-3 border-t bg-gray-50">
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>{activity.date}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{activity.time}</span>
                </div>
              </div>
            )} */}
            {openId === activity.id && (
              <div className="px-6 py-3 border-t bg-gray-50">
                {/* Remarks */}
                {activity.description && (
                  <div className="mb-3 pb-3 border-b border-gray-200">
                    <p className="text-s font-medium text-gray-500 mb-1">Remarks</p>

                    <div
                      className="text-sm text-gray-700 break-words prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: activity.description }}
                    />
                  </div>
                )}

                {/* Date & Time */}
                <div className="flex gap-6 items-center">
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{activity.date}</span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>{activity.time}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Activities;
